import React, { Component, type ComponentType } from 'react';
import Link from '@atlaskit/link';
import { Field as FormField } from '@atlaskit/form';
import { Anchor, Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage } from '@atlassian/jira-intl';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import systemFields from '../../model/system-field.tsx';
import type { FieldOperation, RelatedField, FieldId, Field } from '../../model/types.tsx';
import messages from './messages.tsx';

type RenderJqlUrl = (arg1: Field) => string;

type Props = Intl & {
	fields: FieldOperation[];
	versionName: string;
	renderVersionUrl: RenderJqlUrl;
	MoveVersion: ComponentType<{
		id: FieldId;
	}>;
};

// eslint-disable-next-line jira/react/no-class-components
export class Content extends Component<Props> {
	getFieldName(field: Field): string {
		if (systemFields.includes(field)) {
			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ fixVersion: MessageDescriptorV2; affectedVersion: MessageDescriptorV2; multipleFieldsDescription: MessageDescriptorV2; issueCount: MessageDescriptorV2; oneFieldDescription: MessageDescriptorV2; }'.
			return this.props.intl.formatMessage(messages[field.id]);
		}

		return field.name;
	}

	renderSingleField(): React.JSX.Element {
		const {
			fields: [first],
			intl: { formatMessage },
			MoveVersion,
			renderVersionUrl,
		} = this.props;

		const issueCountLink = fg('replace-anchor-with-link__fusion-solaris') ? (
			<Link href={renderVersionUrl(first.relatedField.field)} target="_blank">
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.issueCountIssueTermRefresh
						: messages.issueCount,
					{
						count: first.relatedField.issueCount,
					},
				)}
			</Link>
		) : (
			<Anchor href={renderVersionUrl(first.relatedField.field)} target="_blank" xcss={anchorStyles}>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.issueCountIssueTermRefresh
						: messages.issueCount,
					{
						count: first.relatedField.issueCount,
					},
				)}
			</Anchor>
		);

		return (
			<>
				<FormattedMessage {...messages.oneFieldDescription} values={{ issueCountLink }} />
				<Box xcss={deleteSingleOptionContainerStyles}>
					<MoveVersion id={first.relatedField.field.id} />
				</Box>
			</>
		);
	}

	renderFieldDescription(relatedField: RelatedField): React.JSX.Element {
		const { renderVersionUrl } = this.props;
		return (
			<>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				{this.getFieldName(relatedField.field)}(
				{fg('replace-anchor-with-link__fusion-solaris') ? (
					<Link target="_blank" href={renderVersionUrl(relatedField.field)}>
						{relatedField.issueCount}
					</Link>
				) : (
					<Anchor target="_blank" href={renderVersionUrl(relatedField.field)} xcss={anchorStyles}>
						{relatedField.issueCount}
					</Anchor>
					// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
				)}
				)
			</>
		);
	}

	renderMultipleFields(): React.JSX.Element {
		const {
			fields,
			intl: { formatMessage },
			MoveVersion,
			versionName,
		} = this.props;
		return (
			<>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.multipleFieldsDescriptionIssueTermRefresh
						: messages.multipleFieldsDescription,
					{ name: versionName },
				)}
				{fields.map(({ relatedField }) => (
					<div key={relatedField.field.id}>
						<FormField
							name={relatedField.field.name}
							label={this.renderFieldDescription(relatedField)}
						>
							{() => <MoveVersion id={relatedField.field.id} />}
						</FormField>
					</div>
				))}
			</>
		);
	}

	render(): React.JSX.Element {
		return this.props.fields.length === 1 ? this.renderSingleField() : this.renderMultipleFields();
	}
}

export default injectIntl(Content);

const deleteSingleOptionContainerStyles = xcss({
	marginTop: 'space.200',
});

const anchorStyles = xcss({
	textDecoration: 'none',
	fontWeight: 'font.weight.medium',
});
